import React, { useMemo } from 'react';
import { useLocation, Navigate, RouteProps } from 'react-router-dom';
import { UserInfo } from 'models/user';
import { isValidRedirectUrl, Routes } from 'fnRoutes';
import {
  useCurrentAccount,
  useCurrentCompany,
  useCurrentUser,
  useSpoof,
} from 'hooks/api';
import { CONSTANTS, Role } from 'constants/common';
import { NotFoundPage } from 'views/NotFoundPage';
import { useFeatureFlagIdentify } from 'hooks/useFeatureFlag';
import { removeAuthCode } from 'helpers/common';
import { AuthErrorPage } from 'views/AuthErrorPage';
import { CONFIG } from 'config';
import { useInvitations } from 'hooks/api/useInvitations';
import { useCheckStripePlan } from 'hooks/api/useStripe';
import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import { useCurrentHousehold } from 'hooks/api/useHousehold';
import { Loading } from '../components/common';
import {
  isRoleNotAllowed,
  progressTrackerRedirect,
  Redirection,
  firstExperienceRedirect,
  wrongHomeRedirect,
  taxOnlyCustomerRedirect,
  invitationsRedirect,
  termNConditionRedirect,
} from './redirects';
import { useFormationsAuth } from './FormationsAuthProvider';

interface WithCoordinatorOptions {
  role?: Role;
  isEnabled?: boolean;
}

const isLoadingData = (
  loadingUser: boolean,
  loadingPayment: boolean,
  loadingHousehold: boolean,
  loadingFormationsAuth: boolean,
  loadingCompany: boolean,
  loadingAccount: boolean,
  loadingInvitations: boolean,
) =>
  loadingUser ||
  loadingPayment ||
  loadingHousehold ||
  loadingFormationsAuth ||
  loadingCompany ||
  loadingAccount ||
  loadingInvitations;

const isSpoofingCustomer = (
  loginUser: UserInfo | undefined,
  isAdminSpoof: boolean,
) => loginUser?.role === CONSTANTS.USER_ROLES.CUSTOMER && isAdminSpoof;

const isPathNameInvalid = (
  pathname: string,
  homeUrl: string,
  isEnabled: boolean,
) => pathname !== homeUrl && !isEnabled;

const withRedirection =
  (
    Component: React.ElementType,
    options: WithCoordinatorOptions = { role: 'any', isEnabled: true },
  ) =>
  (props: RouteProps) => {
    const { isAuthenticated } = useAuth0();
    const { isAdminSpoof } = useSpoof();
    const {
      currentUser,
      loginUser,
      isLoading: isLoadingUser,
      error: currentUserError,
    } = useCurrentUser({
      staleTime: Infinity,
    });
    const { currentAccount, currentCompany, isLoadingFormationsAuth } =
      useFormationsAuth();
    const { isLoading: isLoadingCompany } = useCurrentCompany();
    const { isLoading: isLoadingAccount } = useCurrentAccount();

    const { stripePlan, isLoading: isLoadingPayment } = useCheckStripePlan(
      currentAccount?.id,
    );
    const alreadyPaid = !!stripePlan;
    const { pathname } = useLocation();
    const { role: allowedRole = 'any', isEnabled = true } = options;
    const { currentHousehold: household, isLoading: isLoadingHousehold } =
      useCurrentHousehold();
    const { invitations, isLoading: isLoadingInvitations } = useInvitations();

    if (
      !isAuthenticated ||
      isLoadingData(
        isLoadingUser,
        isLoadingPayment,
        isLoadingHousehold,
        isLoadingFormationsAuth,
        isLoadingCompany,
        isLoadingAccount,
        isLoadingInvitations,
      )
    ) {
      return <Loading />;
    }

    if (currentUser == null) {
      if (!isAuthenticated) {
        // if the user is not authenticated, we need to redirect to the auth page
        const previousUrl = encodeURIComponent(
          window.location.pathname + window.location.search,
        );
        if (isValidRedirectUrl(previousUrl)) {
          window.location.href = `${window.location.origin}/auth?redirectUri=${previousUrl}`;
        } else {
          window.location.href = `${window.location.origin}/auth`;
        }
      } else {
        // if the user is authenticated, we need to redirect to the post login page to let the user and account data be loaded
        return <Navigate to={Routes.POST_LOGIN} replace />;
      }
      // sometimes currentUser is not available at the moment, we could ask customer to refresh the page to have reload the data
      return <AuthErrorPage error={currentUserError} />;
    }
    const { role } = currentUser;
    const homeUrl =
      currentUser.role === CONSTANTS.USER_ROLES.CUSTOMER
        ? Routes.CUSTOMER_HOME
        : Routes.ADMIN_HOME;
    const isHome = [Routes.CUSTOMER_HOME, Routes.ADMIN_HOME].includes(pathname);

    if (isSpoofingCustomer(loginUser, isAdminSpoof)) {
      return <NotFoundPage />;
    }
    // verify path and redirect to proper url if needed
    if (isPathNameInvalid(pathname, homeUrl, isEnabled)) {
      return <NotFoundPage />;
    }
    if (!isHome && isRoleNotAllowed([allowedRole], role, pathname)) {
      return <NotFoundPage />;
    }

    const redirection = new Redirection(
      pathname,
      currentAccount,
      currentUser,
      currentCompany,
      isAdminSpoof,
      alreadyPaid,
      household,
      invitations,
    );

    const newPath = redirection
      .apply(invitationsRedirect)
      .apply(wrongHomeRedirect)
      .apply(taxOnlyCustomerRedirect)
      .apply(firstExperienceRedirect)
      .apply(progressTrackerRedirect)
      .apply(termNConditionRedirect)
      .getPath();

    if (newPath !== pathname) {
      return <Navigate to={newPath} replace />;
    }
    return <Component {...props} />;
  };

type ProtectedRouteProps = {
  role?: Role;
  isEnabled?: boolean;
  component: React.ComponentType<any>;
};
export const ProtectedRoute = ({
  component = () => null,
  role,
  isEnabled,
  ...props
}: ProtectedRouteProps) => {
  const { isAuthenticated } = useAuth0();
  // Use memo to save the protected component as withAuthenticationRequired
  // will generate a new component whenever the url changes
  // It also affect the performance
  const previousUrl = encodeURIComponent(
    window.location.pathname + removeAuthCode(window.location.search),
  );
  const ComponentWithRedirection = withRedirection(component, {
    isEnabled,
    role,
  });
  // load feature flag identity to give user access to feature
  useFeatureFlagIdentify(); // I'm not sure if this is the perfect place to put this function

  const NewComponent = useMemo(
    () =>
      (window as any).Cypress
        ? ComponentWithRedirection
        : withAuthenticationRequired(ComponentWithRedirection, {
            onRedirecting: Loading,
            returnTo:
              !isAuthenticated && isValidRedirectUrl(previousUrl)
                ? previousUrl
                : undefined,
            loginOptions: {
              authorizationParams: {
                redirect_uri: `${window.location.origin}/postlogin`,
                audience: CONFIG.auth0Audience,
              },
            },
          }),
    [component, isAuthenticated],
  );
  return <NewComponent {...props} />;
};
